import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaTablets, FaSyringe, FaStethoscope, FaBriefcaseMedical, FaMicroscope, FaHeartbeat } from 'react-icons/fa';

const Hero = ({ title, subtitle, ctaText, ctaLink, backgroundImage }) => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const floatingIcons = [
    { icon: <FaTablets />, position: "top-1/4 left-1/5", delay: 0, color: "text-primary" },
    { icon: <FaSyringe />, position: "top-1/3 right-1/4", delay: 0.5, color: "text-secondary" },
    { icon: <FaStethoscope />, position: "bottom-1/4 left-1/3", delay: 1, color: "text-primary" },
    { icon: <FaBriefcaseMedical />, position: "top-1/2 right-1/6", delay: 1.5, color: "text-secondary" },
    { icon: <FaMicroscope />, position: "bottom-1/3 right-1/3", delay: 2, color: "text-primary" },
    { icon: <FaHeartbeat />, position: "top-2/3 left-1/6", delay: 2.5, color: "text-secondary" }
  ];

  return (
    <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Background Image with Parallax Effect */}
      <motion.div 
        className="absolute inset-0 z-0"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2 }}
      >
        {/* Professional pharmaceutical gradient background */}
        <div className="w-full h-full bg-gradient-to-br from-blue-900 via-blue-800 to-teal-900">
          {/* Advanced abstract medical elements */}
          <div className="absolute w-full h-full overflow-hidden">
            {/* DNA helix-inspired pattern */}
            <div className="absolute top-0 left-0 w-full h-full opacity-5">
              <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-blue-200 via-blue-400 to-blue-200"></div>
              <div className="absolute top-0 left-1/4 w-32 h-32 rounded-full border-4 border-blue-200 opacity-20 transform translate-y-12"></div>
              <div className="absolute top-0 left-1/4 w-32 h-32 rounded-full border-4 border-blue-200 opacity-20 transform translate-y-52"></div>
              <div className="absolute top-0 left-1/4 w-32 h-32 rounded-full border-4 border-blue-200 opacity-20 transform translate-y-96"></div>
              
              <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-teal-200 via-teal-400 to-teal-200"></div>
              <div className="absolute top-0 right-1/4 w-32 h-32 rounded-full border-4 border-teal-200 opacity-20 transform translate-y-32"></div>
              <div className="absolute top-0 right-1/4 w-32 h-32 rounded-full border-4 border-teal-200 opacity-20 transform translate-y-72"></div>
              <div className="absolute top-0 right-1/4 w-32 h-32 rounded-full border-4 border-teal-200 opacity-20 transform translate-y-112"></div>
            </div>
            
            {/* Glowing orbs representing molecules */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-blue-400 blur-3xl opacity-10"></div>
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-teal-400 blur-3xl opacity-10"></div>
            <div className="absolute top-1/3 right-1/3 w-72 h-72 rounded-full bg-blue-300 blur-3xl opacity-10"></div>
            <div className="absolute bottom-1/3 left-1/3 w-64 h-64 rounded-full bg-teal-300 blur-3xl opacity-10"></div>
          </div>
          
          {/* Subtle dot pattern overlay */}
          <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmZmZmIj48L3JlY3Q+PC9zdmc+')]"></div>
          
          {/* Floating particles effect */}
          <div className="absolute inset-0">
            <div className="absolute h-2 w-2 rounded-full bg-blue-200 opacity-40 top-1/4 left-1/3 animate-pulse"></div>
            <div className="absolute h-2 w-2 rounded-full bg-teal-200 opacity-40 top-1/2 left-1/4 animate-pulse" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute h-2 w-2 rounded-full bg-blue-200 opacity-40 top-3/4 left-2/3 animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute h-2 w-2 rounded-full bg-teal-200 opacity-40 top-1/3 left-3/4 animate-pulse" style={{animationDelay: '1.5s'}}></div>
            <div className="absolute h-2 w-2 rounded-full bg-blue-200 opacity-40 top-2/3 left-1/5 animate-pulse" style={{animationDelay: '2s'}}></div>
          </div>
        </div>
        
        {/* Professional vignette effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/30"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
        
        {/* Light rays effect */}
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-blue-400/5 to-transparent"></div>
        
        {/* Dark Overlay with Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/40"></div>
        
        {/* Enhanced Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5"></div>
        
        {/* Decorative Gradient Overlays - Replacing the pattern dots */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/10 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5"></div>
        
        {/* Decorative Gradient Overlay */}
        <div className="absolute left-0 top-0 w-full h-1/3 bg-gradient-to-b from-primary/20 to-transparent"></div>
        <div className="absolute left-0 bottom-0 w-full h-1/3 bg-gradient-to-t from-secondary/20 to-transparent"></div>
      </motion.div>
      
      {/* Floating Medical Icons */}
      {floatingIcons.map((item, index) => (
        <motion.div
          key={index}
          className={`absolute ${item.position} text-2xl md:text-3xl ${item.color} opacity-70 z-10`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ 
            opacity: 0.7, 
            y: 0,
            transition: { delay: item.delay, duration: 1 }
          }}
          whileInView={{
            y: [0, -10, 0],
            transition: {
              duration: 4,
              repeat: Infinity,
              repeatType: "loop",
              ease: "easeInOut",
              delay: index * 0.3
            }
          }}
        >
          {item.icon}
        </motion.div>
      ))}
      
      {/* Hero Content */}
      <div className="container-custom relative z-10 text-white">
        {/* Decorative Medical Elements - Left Side */}
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 opacity-20 hidden lg:block">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5 }}
            className="relative"
          >
            <div className="h-80 w-80 rounded-full border-4 border-blue-200/30 absolute"></div>
            <div className="h-64 w-64 rounded-full border-4 border-blue-300/30 absolute top-8 left-8"></div>
            <div className="h-48 w-48 rounded-full border-4 border-blue-400/30 absolute top-16 left-16"></div>
            
            {/* DNA Strand */}
            <div className="absolute -top-40 left-16 opacity-30">
              <div className="h-1 w-32 bg-blue-300/70 rotate-45 mb-8"></div>
              <div className="h-1 w-32 bg-blue-300/70 -rotate-45 mb-8"></div>
              <div className="h-1 w-32 bg-blue-300/70 rotate-45 mb-8"></div>
              <div className="h-1 w-32 bg-blue-300/70 -rotate-45 mb-8"></div>
              <div className="h-1 w-32 bg-blue-300/70 rotate-45 mb-8"></div>
            </div>
          </motion.div>
        </div>
        
        {/* Decorative Medical Elements - Right Side */}
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 opacity-20 hidden lg:block">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.3 }}
            className="relative"
          >
            {/* Medical Cross Symbol */}
            <div className="absolute -left-32 top-0">
              <div className="h-32 w-8 bg-teal-300/60 rounded-full"></div>
              <div className="h-8 w-32 bg-teal-300/60 rounded-full -mt-20 -ml-12"></div>
            </div>
            
            {/* Molecule Structure */}
            <div className="h-64 w-64 relative">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-teal-300/80"></div>
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-6 w-6 rounded-full bg-teal-300/80"></div>
              <div className="absolute top-1/2 left-0 transform -translate-y-1/2 h-6 w-6 rounded-full bg-teal-300/80"></div>
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 h-6 w-6 rounded-full bg-teal-300/80"></div>
              <div className="absolute top-1/2 right-0 transform -translate-y-1/2 h-6 w-6 rounded-full bg-teal-300/80"></div>
              
              <div className="absolute top-1/4 left-1/4 h-4 w-4 rounded-full bg-teal-200/60"></div>
              <div className="absolute top-1/4 right-1/4 h-4 w-4 rounded-full bg-teal-200/60"></div>
              <div className="absolute bottom-1/4 left-1/4 h-4 w-4 rounded-full bg-teal-200/60"></div>
              <div className="absolute bottom-1/4 right-1/4 h-4 w-4 rounded-full bg-teal-200/60"></div>
              
              <div className="absolute h-1 w-24 bg-teal-200/60 top-3 left-20 rotate-90"></div>
              <div className="absolute h-1 w-24 bg-teal-200/60 top-32 left-20 rotate-90"></div>
              <div className="absolute h-1 w-24 bg-teal-200/60 top-20 left-3"></div>
              <div className="absolute h-1 w-24 bg-teal-200/60 top-20 right-3"></div>
              
              <div className="absolute h-1 w-16 bg-teal-200/60 top-11 left-11 rotate-45"></div>
              <div className="absolute h-1 w-16 bg-teal-200/60 top-11 right-11 -rotate-45"></div>
              <div className="absolute h-1 w-16 bg-teal-200/60 bottom-11 left-11 -rotate-45"></div>
              <div className="absolute h-1 w-16 bg-teal-200/60 bottom-11 right-11 rotate-45"></div>
            </div>
          </motion.div>
        </div>
        
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.3
                }
              }
            }}
          >
            <motion.span 
              className="inline-block px-4 py-2 border-l-4 border-secondary rounded bg-white/10 backdrop-blur-sm text-sm md:text-base font-medium mb-6 shadow-lg"
              variants={fadeIn}
            >
              <span className="text-blue-300">Leading</span> Pharmaceutical Company since 2012
            </motion.span>
            
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200"
              variants={fadeIn}
            >
              {title || "Advancing Healthcare Through Innovation"}
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed"
              variants={fadeIn}
            >
              {subtitle || "Migliore Life Sciences is dedicated to providing quality pharmaceutical products across Pediatrics, Gynecology, and Orthopedics."}
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap gap-4"
              variants={fadeIn}
            >
              <Link to={ctaLink || "/products"} className="btn-primary group shadow-xl hover:shadow-blue-500/20 transition-all duration-300">
                {ctaText || "Explore Our Products"} 
                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/about" className="btn-outline-light hover:bg-white/10 transition-all duration-300">
                Learn About Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Decorative Bottom Curve */}
      <div className="absolute bottom-0 left-0 w-full h-16 z-10">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-full">
          <path fill="#ffffff" fillOpacity="1" d="M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,250.7C960,267,1056,245,1152,224C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
